import { APP_ID, isDevMode, NgModule, signal } from '@angular/core';
import { MapFeaturePopupService } from '@next/arable-lib';
import {
  GeoBrowserModule,
  InteractionServicesInjector,
  MapFeaturePopUpInjector,
} from '@next/arable-lib/geo';
import { AgrBrowserModule } from '@next/core-lib/browser';

import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';

import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { GeoBrowserModule as InternalGeoBrowserModule } from './geo/geo.browser.module';
import { GeoDrawService } from './geo/services/interactions/geo.draw.service';
import { GeoDrawDivisionService } from './geo/services/interactions/geo.drawDivision.service';
import { GeoDrawOverlapService } from './geo/services/interactions/geo.drawOverlap.service';
import { GeoModifyService } from './geo/services/interactions/geo.modify.service';

import { GeoSelectBlightService } from './geo/services/interactions/geo.selectBlightLayer.service';
import { GeoSelectCropFieldService } from './geo/services/interactions/geo.selectCropField.service';
import { GeoSelectLastYearsFieldService } from './geo/services/interactions/geo.selectLastYearsField.service';
import { GeoSelectRvoService } from './geo/services/interactions/geo.selectRvo.service';
import { GeoSelectShapefileService } from './geo/services/interactions/geo.selectShapefile.service';
import { GeoSelectSoilAnalysisService } from './geo/services/interactions/geo.selectSoilAnalysis.service';
import { parseFieldPlanEnvironment } from './shared/utils/parse-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const buildId = globalThis.CONFIG_BUILD_ID;
    const configUrl = buildId ? `/assets/config.json?v=${buildId}` : '/assets/config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseFieldPlanEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();
export const preserveAgrSessionSnapshot = (): void => {
  try {
    const search = typeof globalThis === 'undefined' ? '' : (globalThis.location?.search ?? '');
    if (!search) return;
    const urlParams = new URLSearchParams(search);
    let agrSession = urlParams.get('AGR_SESSION');
    // If not found, try parsing encoded parameter names
    if (!agrSession) {
      for (const [key] of urlParams.entries()) {
        if (key.includes('AGR_SESSION')) {
          const parts = decodeURIComponent(key).split('=');
          if (parts.length === 2 && parts[0] === 'AGR_SESSION') {
            agrSession = parts[1];
            break;
          }
        }
      }
    }
    if (agrSession) {
      globalThis.sessionStorage?.setItem('AGR_SESSION_SNAPSHOT', agrSession);
    }
  } catch (e) {
    console.warn('Failed to preserve AGR_SESSION snapshot', e);
  }
};
@NgModule({
  imports: [GeoBrowserModule, InternalGeoBrowserModule, AppModule, AgrBrowserModule],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => {
        return loadedConfig();
      },
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    provideEffects(),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode in production
      autoPause: true, // Pauses recording actions when extension window is not open
      trace: false, // Stack trace for actions
      traceLimit: 75, // Maximum stack trace frames
      connectInZone: false, // Connect outside Angular zone to prevent unnecessary change detection
    }),
    {
      provide: InteractionServicesInjector,
      useClass: GeoDrawService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoDrawDivisionService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoDrawOverlapService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoModifyService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectCropFieldService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectBlightService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectSoilAnalysisService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectRvoService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectLastYearsFieldService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectShapefileService,
      multi: true,
    },
    {
      provide: MapFeaturePopUpInjector,
      useClass: MapFeaturePopupService,
    },
  ],
})
export class AppBrowserModule {}
